import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, 
  Building2, 
  FolderOpen, 
  UserPlus, 
  Plus, 
  X, 
  ChevronDown,
  ChevronUp,
  Save,
  ArrowRight,
  User,
  Crown
} from 'lucide-react';

const CreateDepartmentWithTeams = ({ onClose, onSuccess }) => {
  const { users, createDepartment, createTeam, updateUser } = useData();
  const { currentUser } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    description: '',
    managerIds: [],
    isActive: true
  });
  
  const [teams, setTeams] = useState([]);
  const [newTeam, setNewTeam] = useState({
    name: '',
    managerId: '',
    members: []
  });
  
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState({});

  const handleDepartmentChange = (field, value) => {
    setDepartmentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleManagerToggle = (managerId) => {
    setDepartmentForm(prev => ({
      ...prev,
      managerIds: prev.managerIds.includes(managerId)
        ? prev.managerIds.filter(id => id !== managerId)
        : [...prev.managerIds, managerId]
    }));
  };

  const addTeam = () => {
    if (newTeam.name.trim()) {
      const teamData = {
        ...newTeam,
        id: `temp_${Date.now()}`,
        departmentId: 'temp_department' // Will be updated after department creation
      };
      setTeams(prev => [...prev, teamData]);
      setNewTeam({ name: '', managerId: '', members: [] });
      setShowTeamForm(false);
    }
  };

  const removeTeam = (teamId) => {
    setTeams(prev => prev.filter(team => team.id !== teamId));
  };

  const handleTeamMemberToggle = (teamId, memberId) => {
    setTeams(prev => prev.map(team => {
      if (team.id === teamId) {
        const members = team.members.includes(memberId)
          ? team.members.filter(id => id !== memberId)
          : [...team.members, memberId];
        return { ...team, members };
      }
      return team;
    }));
  };

  const handleTeamManagerChange = (teamId, managerId) => {
    setTeams(prev => prev.map(team => {
      if (team.id === teamId) {
        return { ...team, managerId };
      }
      return team;
    }));
  };

  const toggleTeamExpansion = (teamId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Create department first
      const departmentData = {
        name: departmentForm.name,
        description: departmentForm.description,
        managerIds: departmentForm.managerIds,
        teamMembers: [],
        isActive: departmentForm.isActive
      };
      
      const newDepartmentId = await createDepartment(departmentData);
      
      // Update managers' department
      for (const managerId of departmentForm.managerIds) {
        await updateUser(managerId, { departmentId: newDepartmentId });
      }
      
      // Create teams and assign members
      const createdTeams = [];
      for (const team of teams) {
        const teamData = {
          name: team.name,
          departmentId: newDepartmentId,
          managerId: team.managerId || null,
          description: `${team.name} - צוות במחלקת ${departmentForm.name}`
        };
        
        const newTeamId = await createTeam(teamData);
        createdTeams.push({ ...team, id: newTeamId });
        
        // Update team manager
        if (team.managerId) {
          await updateUser(team.managerId, { teamId: newTeamId });
        }
        
        // Update team members
        for (const memberId of team.members) {
          await updateUser(memberId, { teamId: newTeamId });
        }
      }
      
      onSuccess?.({
        department: { ...departmentData, id: newDepartmentId },
        teams: createdTeams
      });
      
      alert(`המחלקה "${departmentForm.name}" נוצרה בהצלחה עם ${teams.length} צוותים!`);
      onClose();
      
    } catch (error) {
      console.error('Error creating department with teams:', error);
      alert('שגיאה ביצירת המחלקה. נסה שוב.');
    }
  };

  const getAvailableUsers = () => {
    // תמיכה במספר מחלקות - משתמשים יכולים להיות במספר מחלקות
    return users.filter(user => 
      user.role !== 'org_admin' // Exclude org admin
    );
  };

  const getAvailableManagers = () => {
    // תמיכה במספר מחלקות - מנהלים יכולים להיות במספר מחלקות
    return users.filter(user => 
      (user.role === 'dept_manager' || user.role === 'team_manager' || user.role === 'ceo' || user.role === 'deputy_ceo')
    );
  };

  const getAvailableTeamMembers = () => {
    // תמיכה במספר צוותים - משתמשים יכולים להיות במספר צוותים
    return users.filter(user => 
      user.role !== 'org_admin' // Exclude org admin
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">יצירת מחלקה עם צוותים</h2>
              <p className="text-blue-100 mt-1">צור מחלקה חדשה עם צוותים וחברי צוות</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center mt-4 space-x-4">
            <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-white' : 'text-blue-200'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-white text-blue-600' : 'bg-blue-500 text-white'}`}>
                1
              </div>
              <span className="text-sm font-medium">פרטי מחלקה</span>
            </div>
            <ArrowRight className="w-4 h-4 text-blue-200" />
            <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-white' : 'text-blue-200'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-white text-blue-600' : 'bg-blue-500 text-white'}`}>
                2
              </div>
              <span className="text-sm font-medium">צוותים</span>
            </div>
            <ArrowRight className="w-4 h-4 text-blue-200" />
            <div className={`flex items-center space-x-2 ${currentStep >= 3 ? 'text-white' : 'text-blue-200'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-white text-blue-600' : 'bg-blue-500 text-white'}`}>
                3
              </div>
              <span className="text-sm font-medium">סיכום</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Department Details */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">פרטי המחלקה</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-500" />
                    שם המחלקה *
                  </label>
                  <input
                    type="text"
                    value={departmentForm.name}
                    onChange={(e) => handleDepartmentChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="הכנס שם המחלקה"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-gray-500" />
                    תיאור (אופציונלי)
                  </label>
                  <textarea
                    value={departmentForm.description}
                    onChange={(e) => handleDepartmentChange('description', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24 resize-none"
                    placeholder="תאר את המחלקה בקצרה..."
                  />
                </div>
              </div>

              {/* Department Managers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-gray-500" />
                  מנהלי המחלקה
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {getAvailableManagers().map(manager => (
                    <label key={manager.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={departmentForm.managerIds.includes(manager.id)}
                        onChange={() => handleManagerToggle(manager.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{manager.name}</div>
                        <div className="text-sm text-gray-500">{manager.email}</div>
                        <div className="text-xs text-blue-600">
                          {manager.role === 'ceo' ? 'מנכ"ל' :
                           manager.role === 'deputy_ceo' ? 'סמנכ"ל' :
                           manager.role === 'dept_manager' ? 'מנהל מחלקה' :
                           manager.role === 'team_manager' ? 'מנהל צוות' : 'עובד'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Teams */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">צוותים במחלקה</h3>
                <button
                  type="button"
                  onClick={() => setShowTeamForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  הוסף צוות
                </button>
              </div>

              {/* New Team Form */}
              {showTeamForm && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-4">צוות חדש</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        שם הצוות *
                      </label>
                      <input
                        type="text"
                        value={newTeam.name}
                        onChange={(e) => setNewTeam(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="הכנס שם הצוות"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        מנהל צוות
                      </label>
                      <select
                        value={newTeam.managerId}
                        onChange={(e) => setNewTeam(prev => ({ ...prev, managerId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">בחר מנהל צוות</option>
                        {getAvailableUsers().filter(user => 
                          user.role === 'team_manager' || user.role === 'dept_manager'
                        ).map(user => (
                          <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      חברי הצוות
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                      {getAvailableTeamMembers().map(member => (
                        <label key={member.id} className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newTeam.members.includes(member.id)}
                            onChange={(e) => {
                              const members = e.target.checked
                                ? [...newTeam.members, member.id]
                                : newTeam.members.filter(id => id !== member.id);
                              setNewTeam(prev => ({ ...prev, members }));
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-900">{member.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowTeamForm(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      ביטול
                    </button>
                    <button
                      type="button"
                      onClick={addTeam}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      הוסף צוות
                    </button>
                  </div>
                </div>
              )}

              {/* Existing Teams */}
              <div className="space-y-4">
                {teams.map(team => (
                  <div key={team.id} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <Users className="w-5 h-5 text-blue-500" />
                        <h4 className="font-semibold text-gray-900">{team.name}</h4>
                        <span className="text-sm text-gray-500">
                          {team.members.length} חברים
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => toggleTeamExpansion(team.id)}
                          className="p-1 hover:bg-gray-100 rounded-lg"
                        >
                          {expandedTeams[team.id] ? 
                            <ChevronUp className="w-4 h-4 text-gray-500" /> :
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          }
                        </button>
                        <button
                          type="button"
                          onClick={() => removeTeam(team.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {expandedTeams[team.id] && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            מנהל צוות
                          </label>
                          <select
                            value={team.managerId}
                            onChange={(e) => handleTeamManagerChange(team.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">בחר מנהל צוות</option>
                            {getAvailableUsers().filter(user => 
                              user.role === 'team_manager' || user.role === 'dept_manager'
                            ).map(user => (
                              <option key={user.id} value={user.id}>{user.name}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            חברי הצוות
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                            {getAvailableTeamMembers().map(member => (
                              <label key={member.id} className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={team.members.includes(member.id)}
                                  onChange={() => handleTeamMemberToggle(team.id, member.id)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-900">{member.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Summary */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">סיכום</h3>
              
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  {departmentForm.name}
                </h4>
                {departmentForm.description && (
                  <p className="text-blue-800 text-sm mb-3">{departmentForm.description}</p>
                )}
                <div className="text-sm text-blue-700">
                  <p>מנהלי מחלקה: {departmentForm.managerIds.length}</p>
                  <p>צוותים: {teams.length}</p>
                  <p>סה"כ חברי צוות: {teams.reduce((sum, team) => sum + team.members.length, 0)}</p>
                </div>
              </div>
              
              {teams.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">צוותים:</h4>
                  {teams.map(team => (
                    <div key={team.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="font-medium text-gray-900">{team.name}</div>
                      <div className="text-sm text-gray-600">
                        חברים: {team.members.length} | 
                        מנהל: {team.managerId ? getAvailableUsers().find(u => u.id === team.managerId)?.name : 'לא נבחר'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <div>
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  חזור
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
              >
                ביטול
              </button>
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  disabled={currentStep === 1 && !departmentForm.name.trim()}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  המשך
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  צור מחלקה
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDepartmentWithTeams;
